/**
 * FreeToolFunnelWidget
 * =====================
 * Admin-only ranked card grid showing per-tool signups (all-time / 30d / 7d)
 * with unique-domain count + top domains.
 *
 * Surfaces on /admin/leads at the top so the founder sees "which lead
 * magnet is pulling its weight this week" at a glance every Monday.
 *
 * Hits `GET /api/admin/free-tool-funnel`. Fails quietly so the parent
 * page (AdminLeads) still renders even if this fails.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Loader2, RefreshCw, TrendingUp, ArrowUpRight, BarChart3,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const FreeToolFunnelWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/free-tool-funnel`, { headers: authHeaders() });
      setData(data);
    } catch (e) {
      console.debug('free-tool-funnel unavailable:', e?.message);
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <Card className="bg-slate-900 border-slate-800 mb-6" data-testid="funnel-widget-loading">
        <CardContent className="p-5 flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading funnel data…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const tools = data.tools_ranked_by_momentum || [];
  const totals = data.totals || {};

  return (
    <Card className="bg-gradient-to-br from-emerald-500/[0.06] via-slate-900/70 to-slate-900/70 border-emerald-500/25 mb-6" data-testid="funnel-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Free-tool funnel</h2>
              <p className="text-xs text-slate-400">
                {totals.all_signups_all_tools?.toLocaleString() || 0} total signups across {tools.length} tools ·{' '}
                <span className="text-emerald-300 font-semibold">{totals.last_30d_all_tools?.toLocaleString() || 0}</span> in last 30d ·{' '}
                <span className="text-cyan-300 font-semibold">{totals.last_7d_all_tools?.toLocaleString() || 0}</span> in last 7d
              </p>
            </div>
          </div>
          <Button
            onClick={load}
            disabled={refreshing}
            size="sm"
            variant="outline"
            className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
            data-testid="funnel-widget-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="funnel-widget-grid">
          {tools.map((t, i) => {
            const has30d = (t.last_30d || 0) > 0;
            const has7d = (t.last_7d || 0) > 0;
            const accent = i === 0 && has30d
              ? 'border-emerald-500/40 bg-emerald-500/5'
              : has30d
                ? 'border-slate-700 hover:border-emerald-500/30'
                : 'border-slate-800 opacity-70';
            return (
              <a
                key={t.magnet_slug}
                href={t.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`funnel-card-${t.magnet_slug}`}
                className={`group relative rounded-lg p-4 bg-slate-950 border ${accent} transition-all hover:bg-emerald-500/[0.05]`}
              >
                {i === 0 && has30d && (
                  <Badge className="absolute -top-2 right-3 bg-emerald-500 text-slate-950 border-0 text-[11px] tracking-widest font-bold">
                    🥇 LEADER
                  </Badge>
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white truncate pr-2">{t.label}</span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-emerald-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
                </div>
                <code className="text-[11px] text-slate-400 font-mono">{t.url}</code>
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-white/5">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-slate-400">Total</p>
                    <p className="text-base font-bold text-white tabular-nums">{(t.total_signups || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-slate-400">30d</p>
                    <p className={`text-base font-bold tabular-nums ${has30d ? 'text-emerald-400' : 'text-slate-400'}`}>
                      {(t.last_30d || 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-slate-400">7d</p>
                    <p className={`text-base font-bold tabular-nums ${has7d ? 'text-cyan-400' : 'text-slate-400'}`}>
                      {(t.last_7d || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                {(t.unique_company_domains || 0) > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <p className="text-[11px] text-slate-400">
                      <TrendingUp className="w-3 h-3 inline mr-1 text-emerald-500" />
                      <span className="text-white font-semibold">{t.unique_company_domains}</span> unique companies
                      {t.top_domains?.[0]?.domain && (
                        <span className="text-slate-400 ml-1">
                          · top: <span className="text-emerald-300 font-mono">@{t.top_domains[0].domain}</span>
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {(t.total_signups || 0) === 0 && (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <p className="text-[11px] text-amber-400/80 italic">No signups yet — worth re-placing on the landing page.</p>
                  </div>
                )}
              </a>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 mt-4 text-center italic">
          Updated {data.generated_at ? new Date(data.generated_at).toLocaleString() : '—'} · counts are live, no cache
        </p>
      </CardContent>
    </Card>
  );
};

export default FreeToolFunnelWidget;
